/**
 * Platform Admin shell. Auth is username/password (no LINE) via
 * POST /platform/login — operator credentials live in `platform_admins`.
 * A valid login mints a platform JWT (tenant_id='platform',
 * platform_admin=true) that gates every /platform/* route.
 *
 * main.tsx already wraps the app in <BrowserRouter>, so this file only
 * declares <Routes> + a top nav.
 */
import { useState } from 'react';
import { Routes, Route, Navigate, Link } from 'react-router-dom';
import { useAuthStore } from './lib/store';
import { apiFetch, ApiError } from './lib/api';
import { TenantList } from './routes/TenantList';
import { TenantDetail } from './routes/TenantDetail';
import { ProductRequests } from './routes/ProductRequests';
import { Feedback } from './routes/Feedback';
import { Settings } from './routes/Settings';
import type { AuthProfile } from '@payroll/shared';

interface LoginResult {
  jwt: string;
  profile: { admin_id: string; email: string; display_name: string | null; is_platform_admin: boolean };
}

function LoginScreen({ onLogin }: { onLogin: (jwt: string, profile: AuthProfile) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await apiFetch<LoginResult>('/platform/login', {
        method: 'POST',
        body: { email: email.trim(), password },
        skipAuth: true,
      });
      // Map operator profile → AuthProfile shape the store expects.
      onLogin(r.jwt, {
        user_id: r.profile.admin_id,
        email: r.profile.email,
        display_name: r.profile.display_name,
        is_platform_admin: r.profile.is_platform_admin,
      } as unknown as AuthProfile);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'เข้าสู่ระบบไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <form onSubmit={submit} className="card p-6 w-full max-w-sm space-y-4">
        <div className="text-center">
          <div className="text-xs uppercase tracking-wider text-gray-400">Payroll Edge</div>
          <h1 className="text-lg font-semibold">Platform Admin</h1>
          <p className="text-xs text-gray-500 mt-1">เข้าสู่ระบบด้วยอีเมลและรหัสผ่าน</p>
        </div>
        <div>
          <label className="text-xs text-gray-500">อีเมล</label>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1"
            required
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">รหัสผ่าน</label>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1"
            required
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? 'กำลังเข้าสู่ระบบ…' : 'เข้าสู่ระบบ'}
        </button>
      </form>
    </div>
  );
}

export function App() {
  const { profile, jwt, setAuth, clear } = useAuthStore();

  if (!jwt || !profile) {
    return <LoginScreen onLogin={setAuth} />;
  }

  return (
    <>
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <Link to="/tenants" className="font-medium hover:text-brand-700">บริษัท</Link>
            <Link to="/requests" className="font-medium hover:text-brand-700">คำขอใช้งานแอป</Link>
            <Link to="/feedback" className="font-medium hover:text-brand-700">บั๊ก & ฟีเจอร์</Link>
            <Link to="/settings" className="font-medium hover:text-brand-700">ตั้งค่า</Link>
          </div>
          <div className="flex items-center gap-3 text-gray-500">
            <span className="hidden sm:inline">{profile.display_name ?? profile.email ?? ''}</span>
            <button onClick={clear} className="hover:text-red-600">ออกจากระบบ</button>
          </div>
        </div>
      </div>
      <Routes>
        <Route path="/" element={<Navigate to="/tenants" replace />} />
        <Route path="/tenants" element={<TenantList />} />
        <Route path="/tenants/:id" element={<TenantDetail />} />
        <Route path="/requests" element={<ProductRequests />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/tenants" replace />} />
      </Routes>
    </>
  );
}
